import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Team, Report } from '../types';
import { Card, Button, FlexContainer } from '../styles/GlobalStyles';
import { reportService, DateRange } from '../services/reportService';
import { teamService } from '../services/teamService';
import { assessmentService } from '../services/assessmentService';
import { LoadingSpinner } from './LoadingSpinner';

const DashboardContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
`;

const DashboardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: var(--spacing-lg);
  
  @media (max-width: 767px) {
    flex-direction: column;
    gap: var(--spacing-md);
  }
`;

const DashboardTitle = styled.h2`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const DashboardSubtitle = styled.div`
  color: var(--text-secondary);
  font-size: 0.875rem;
  margin-top: var(--spacing-xs);
`;

const FilterControls = styled.div`
  display: flex;
  gap: var(--spacing-md);
  align-items: center;
  margin-bottom: var(--spacing-lg);
  flex-wrap: wrap;
  
  @media (max-width: 767px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
`;

const FilterLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-primary);
`;

const DateInput = styled.input`
  padding: var(--spacing-sm);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  font-size: 0.875rem;
  
  &:focus {
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
    outline: none;
  }
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: var(--spacing-md);
  margin-bottom: var(--spacing-lg);
`;

const MetricCard = styled(Card)`
  padding: var(--spacing-lg);
  text-align: center;
`;

const MetricValue = styled.div<{ color?: string }>`
  font-size: 2rem;
  font-weight: 700;
  color: ${props => props.color || 'var(--text-primary)'};
  margin-bottom: var(--spacing-xs);
`;

const MetricLabel = styled.div`
  font-size: 0.875rem;
  color: var(--text-secondary);
  font-weight: 500;
`;

const ChartsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: var(--spacing-lg);
  margin-bottom: var(--spacing-lg);
`;

const ChartCard = styled(Card)`
  padding: var(--spacing-lg);
`;

const ChartTitle = styled.h3`
  margin: 0 0 var(--spacing-md) 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const ChartPlaceholder = styled.div`
  width: 100%;
  height: 300px;
  background: var(--background-secondary);
  border: 2px dashed var(--border-color);
  border-radius: var(--border-radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary);
  font-size: 0.875rem;
  text-align: center;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: var(--spacing-2xl);
  color: var(--text-secondary);
`;

interface ClubAnalyticsDashboardProps {
  clubId: string;
}

interface TeamAnalytics {
  team: Team;
  playerCount: number;
  assessmentCount: number;
  averageScore: number;
  lastAssessmentDate?: Date;
  coachName?: string;
}

export const ClubAnalyticsDashboard: React.FC<ClubAnalyticsDashboardProps> = ({
  clubId
}) => {
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [teamAnalytics, setTeamAnalytics] = useState<TeamAnalytics[]>([]);
  const [clubReports, setClubReports] = useState<Report[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    loadClubAnalytics();
    loadClubReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubId]);

  const loadClubAnalytics = async () => {
    try {
      setIsLoading(true);
      
      // Get all teams in the club
      const teams = await teamService.getTeams(); // This would need to be filtered by clubId in real implementation
      const analytics: TeamAnalytics[] = [];

      for (const team of teams) {
        try {
          const players = await teamService.getTeamPlayers(team.id);
          let totalAssessments = 0;
          let totalScore = 0;
          let scoreCount = 0;
          let lastAssessmentDate: Date | undefined;

          for (const player of players) {
            try {
              const assessments = await assessmentService.getPlayerAssessments(player.id);
              totalAssessments += assessments.length;

              if (assessments.length > 0) {
                const latestAssessment = assessments[0];
                
                // Calculate average score for this assessment
                const allScores: number[] = [];
                Object.values(latestAssessment.scores).forEach(corner => {
                  Object.values(corner as Record<string, number>).forEach(score => {
                    allScores.push(score);
                  });
                });
                
                if (allScores.length > 0) {
                  totalScore += allScores.reduce((sum, score) => sum + score, 0) / allScores.length;
                  scoreCount++;
                }

                const assessmentDate = new Date(latestAssessment.assessmentDate);
                if (!lastAssessmentDate || assessmentDate > lastAssessmentDate) {
                  lastAssessmentDate = assessmentDate;
                }
              }
            } catch (error) {
              console.error(`Error loading assessments for player ${player.id}:`, error);
            }
          }

          analytics.push({
            team,
            playerCount: players.length,
            assessmentCount: totalAssessments,
            averageScore: scoreCount > 0 ? totalScore / scoreCount : 0,
            lastAssessmentDate,
            coachName: 'Coach Name' // This would come from user data in real implementation
          });
        } catch (error) {
          console.error(`Error loading analytics for team ${team.id}:`, error);
          analytics.push({
            team,
            playerCount: 0,
            assessmentCount: 0,
            averageScore: 0,
            coachName: 'Coach Name'
          });
        }
      }

      setTeamAnalytics(analytics);
    } catch (error) {
      console.error('Error loading club analytics:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadClubReports = async () => {
    try {
      await reportService.generateClubReport(clubId);
      // In real implementation, this would get existing club reports
      setClubReports([]);
    } catch (error) {
      console.error('Error loading club reports:', error);
    }
  };

  const handleGenerateClubReport = async () => {
    try {
      setIsGenerating(true);
      const report = await reportService.generateClubReport(clubId, { dateRange });
      setClubReports([report, ...clubReports]);
    } catch (error) {
      console.error('Error generating club report:', error);
      alert('Failed to generate club report. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownloadReport = async (reportId: string) => {
    try {
      await reportService.downloadReport(reportId);
    } catch (error) {
      console.error('Error downloading report:', error);
      alert('Failed to download report. Please try again.');
    }
  };

  const getFilteredAndSortedTeams = () => {
    // For single team view, just return all teams sorted by name
    return teamAnalytics.sort((a, b) => a.team.name.localeCompare(b.team.name));
  };

  const calculateClubMetrics = () => {
    const totalPlayers = teamAnalytics.reduce((sum, t) => sum + t.playerCount, 0);
    const totalAssessments = teamAnalytics.reduce((sum, t) => sum + t.assessmentCount, 0);
    const totalTeams = teamAnalytics.length;
    
    const teamsWithScores = teamAnalytics.filter(t => t.averageScore > 0);
    const clubAverageScore = teamsWithScores.length > 0 
      ? teamsWithScores.reduce((sum, t) => sum + t.averageScore, 0) / teamsWithScores.length 
      : 0;

    return {
      totalPlayers,
      totalAssessments,
      totalTeams,
      clubAverageScore
    };
  };

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const filteredTeams = getFilteredAndSortedTeams();
  const clubMetrics = calculateClubMetrics();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <DashboardContainer>
      <DashboardHeader>
        <div>
          <DashboardTitle>Team Analytics Dashboard</DashboardTitle>
          <DashboardSubtitle>
            Team performance metrics
          </DashboardSubtitle>
        </div>
        
        <FlexContainer $gap="var(--spacing-sm)">
          <Button
            onClick={handleGenerateClubReport}
            disabled={isGenerating || clubMetrics.totalAssessments === 0}
          >
            {isGenerating ? 'Generating...' : 'Generate Team Report'}
          </Button>
        </FlexContainer>
      </DashboardHeader>

      <FilterControls>
        <FilterGroup>
          <FilterLabel>From Date</FilterLabel>
          <DateInput
            type="date"
            value={dateRange?.startDate ? dateRange.startDate.toISOString().split('T')[0] : ''}
            onChange={(e) => setDateRange(prev => ({
              ...prev,
              startDate: new Date(e.target.value)
            } as DateRange))}
          />
        </FilterGroup>

        <FilterGroup>
          <FilterLabel>To Date</FilterLabel>
          <DateInput
            type="date"
            value={dateRange?.endDate ? dateRange.endDate.toISOString().split('T')[0] : ''}
            onChange={(e) => setDateRange(prev => ({
              ...prev,
              endDate: new Date(e.target.value)
            } as DateRange))}
          />
        </FilterGroup>
      </FilterControls>

      <MetricsGrid>
        <MetricCard>
          <MetricValue color="var(--success-color)">{clubMetrics.totalPlayers}</MetricValue>
          <MetricLabel>Total Players</MetricLabel>
        </MetricCard>
        
        <MetricCard>
          <MetricValue color="var(--warning-color)">{clubMetrics.totalAssessments}</MetricValue>
          <MetricLabel>Total Assessments</MetricLabel>
        </MetricCard>
        
        <MetricCard>
          <MetricValue>{clubMetrics.clubAverageScore.toFixed(1)}</MetricValue>
          <MetricLabel>Team Average Score</MetricLabel>
        </MetricCard>
      </MetricsGrid>

      <ChartsContainer>
        <ChartCard>
          <ChartTitle>Team Performance Trends</ChartTitle>
          <ChartPlaceholder>
            Line chart showing team performance trends over time
            <br />
            <small>(Chart visualization would be implemented here)</small>
          </ChartPlaceholder>
        </ChartCard>
        
        <ChartCard>
          <ChartTitle>Assessment Activity</ChartTitle>
          <ChartPlaceholder>
            Chart showing assessment frequency for the team
            <br />
            <small>(Assessment activity chart would be implemented here)</small>
          </ChartPlaceholder>
        </ChartCard>
      </ChartsContainer>

      {filteredTeams.length === 0 ? (
        <EmptyState>
          <h3>No team data available</h3>
          <p>Complete assessments to see team analytics.</p>
        </EmptyState>
      ) : null}

      {clubReports.length > 0 && (
        <Card>
          <ChartTitle>Recent Club Reports</ChartTitle>
          <FlexContainer $direction="column" $gap="var(--spacing-sm)">
            {clubReports.slice(0, 3).map((report) => (
              <FlexContainer key={report.id} $justify="space-between" $align="center">
                <div>
                  <div>{reportService.formatReportType(report.type)} - {formatDate(report.createdAt)}</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                    {reportService.isReportExpired(report) ? 'Expired' : 'Available'}
                  </div>
                </div>
                <Button
                  $size="small"
                  $variant="secondary"
                  disabled={reportService.isReportExpired(report)}
                  onClick={() => handleDownloadReport(report.id)}
                >
                  Download
                </Button>
              </FlexContainer>
            ))}
          </FlexContainer>
        </Card>
      )}
    </DashboardContainer>
  );
};